import { formatOvenTemperature } from './formatting';

/**
 * Le four — le nommer, et n'en parler que quand l'étape en parle (US-100).
 *
 * ### Le défaut
 *
 * Sur la fiche, « cuire 7 min / 90 °C / vitesse 3 » s'affichait
 * « 7 min · 90 °C (th. 3) ». Le thermostat est une convention **de four** —
 * la position N vaut N × 30 °C sur un four domestique français — et elle n'a
 * aucun sens sur la température d'un bol chauffant. Ce n'était pas faux au
 * sens d'une valeur erronée : 90 °C reste 90 °C. C'était faux au sens d'un
 * **repère proposé pour un appareil qu'on n'utilise pas**, sur les 342 étapes
 * du fonds Thermomix qui portent une température.
 *
 * ### La règle
 *
 * Le thermostat ne s'affiche que si l'étape **nomme** un four. C'est la même
 * exigence que partout ailleurs dans ce dépôt : on ne devine pas. Et
 * l'asymétrie est voulue — ne rien dire n'est jamais faux, tandis que proposer
 * une position de thermostat sur un bol l'est toujours. Dans le doute, la
 * température s'affiche seule.
 *
 * ### Un seul vocabulaire
 *
 * `computeAutoTags.ts` portait le sien, `/\bfours?\b/i`, pour l'étiquette
 * « sans four ». Deux listes de mots pour une même notion divergent à la
 * première story qui en enrichit une : celle-ci est donc la seule, et la règle
 * d'étiquette la lit aussi.
 *
 * **Mesuré avant d'être écrit** (30 août 2026, bibliothèque réelle, 231
 * recettes) : l'enrichissement du vocabulaire — « enfourner », « thermostat »,
 * « chaleur tournante » en plus de « four » — ne change l'étiquette « sans
 * four » d'**aucune** recette. Toute recette qui dit « enfourner » dit aussi
 * « four » quelque part, ou porte une température. Le vocabulaire s'unifie
 * donc sans déplacer une seule étiquette, et sans migration de recalcul.
 */

/**
 * Ce qui, dans le texte d'une étape, nomme un four.
 *
 * - `fours?` en mot entier : ni « fourchette », ni « fourrer », ni « enfourner »
 *   — ce dernier est couvert par son propre motif, plus bas.
 * - `enfourn\w*` : « enfourner », « enfournez », « enfournée ».
 * - `thermostats?` et `th. 6` : un texte qui donne une position de thermostat
 *   parle d'un four par définition.
 * - `chaleur tournante` : le mode d'un four, et de rien d'autre.
 *
 * **Écarté : « préchauffer » seul.** On préchauffe aussi une poêle, une
 * plancha, un gril — et aucun de ces appareils n'a de thermostat gradué. Le
 * mot arrive de toute façon presque toujours avec « le four », que le premier
 * motif attrape. Écarté aussi : « gril », qui est le plus souvent un barbecue.
 *
 * Insensible à la casse. Les accents ne posent pas de question ici : aucun des
 * mots retenus n'en porte.
 */
const MENTION_DE_FOUR = /\bfours?\b|\benfourn\w*|\bthermostats?\b|\bth\.\s*\d|chaleur\s+tournante/i;

/** Le texte de cette étape nomme-t-il un four ? */
export function mentionneUnFour(texte: string): boolean {
  return MENTION_DE_FOUR.test(texte);
}

/**
 * La température d'une étape, telle qu'elle s'affiche : les degrés toujours,
 * la position de thermostat **seulement si l'étape parle d'un four**.
 *
 * C'est la fonction que les écrans appellent ; `formatOvenTemperature` reste
 * ce qu'elle a toujours été — la mise en forme d'une température **de four** —
 * et n'a plus à décider si c'en est une.
 */
export function formatTemperatureDEtape(celsius: number | null, instruction: string): string | null {
  if (celsius === null || !Number.isFinite(celsius)) return null;
  if (mentionneUnFour(instruction)) return formatOvenTemperature(celsius);
  return `${String(celsius)} °C`;
}
